import React from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { TextInput } from '@portal_components/components/TextInput';
import { TableItem } from '@portal_components/components/TableItem';
import { CustomerSearchResult } from '../elements/customerSearch/CustomerSearchResult';
import { EnvironmentVariables } from '../EnvironmentVariables';
import { usePortalQuery, EHttpMethod } from '../hooks/usePortalQuery';
import { useForm, FormProvider } from 'react-hook-form';
import { FormButton } from '@portal_components/components/FormButton';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import { PageHeading } from '@portal_components/components/PageHeading';
import { Button } from '@portal_components/components/Button';
import { EUserRole } from '@portal_components/types';
import { useUserInfo } from '../hooks/useUserInfo';

import { Contract, CustomerSearch, Group } from '../types';

export const CustomerSearchPage = () => {
  const { userInfo } = useUserInfo();

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchResults, setSearchResults] = React.useState<{ group: Group; contracts: Contract[] }[] | null>(null);
  const [isSearchResultsLoading, setIsSearchResultsLoading] = React.useState<boolean>(false);
  const [expandedContractIndex, setExpandedContractIndex] = React.useState<number | null>(null);
  const [expandedGroupIndex, setExpandedGroupIndex] = React.useState<number | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const { data: groupData, isLoading: isGroupDataLoading } = usePortalQuery<{ customerGroupName: string; customerGroupId: string }[]>(
    EHttpMethod.GET,
    'search-groups',
    'search-groups',
    true,
    undefined,
    undefined,
    () => {
      setErrorMessage('An error occurred while loading groups.');
    },
    false,
    600000,
    600000
  );

  const methods = useForm<CustomerSearch>({
    values: {
      searchCustomerGroupId: '',
      searchSapNumber: ''
    }
  });

  const readSearchFromParams = React.useCallback((): CustomerSearch => {
    const customerGroupId = searchParams.get('customerGroupId') ?? '';
    const sapNumber = searchParams.get('sapNumber') ?? '';

    return {
      searchCustomerGroupId: customerGroupId,
      searchSapNumber: sapNumber
    };
  }, [searchParams]);

  const writeParamsFromSearch = React.useCallback(
    (values: CustomerSearch) => {
      const params: Record<string, string> = {};

      if (values.searchCustomerGroupId) params.customerGroupId = values.searchCustomerGroupId;

      if (values.searchSapNumber) params.sapNumber = values.searchSapNumber;

      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const handleSearch = React.useCallback(
    async (values: CustomerSearch) => {
      if (isGroupDataLoading) return;

      setExpandedContractIndex(null);
      setExpandedGroupIndex(null);
      setSearchResults(null);
      setErrorMessage(null);
      setIsSearchResultsLoading(true);

      if (!values.searchCustomerGroupId && !values.searchSapNumber) {
        setErrorMessage('Select customer group name or SAP number to search');
        setIsSearchResultsLoading(false);
        return;
      }

      const response = await axios
        .post<
          {
            group: Group;
            contracts: Contract[];
          }[]
        >(`${EnvironmentVariables.ApiUrl}/customer-search`, values, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        })
        .catch(error => error?.response);

      setIsSearchResultsLoading(false);

      if (!response) {
        setErrorMessage('An error occurred while searching for customers.');
        return;
      }
      if (response.status !== 200) {
        setErrorMessage(response.data);
        return;
      }
      if (!response.data) {
        setErrorMessage('No results found.');
        return;
      }

      setSearchResults(response.data);
      lastSearchRef.current = values;
    },
    [isGroupDataLoading]
  );

  const handleSearchSubmit = React.useCallback(
    async (values: CustomerSearch) => {
      writeParamsFromSearch(values);
      await handleSearch(values);
    },
    [writeParamsFromSearch, handleSearch]
  );

  const handleReset = React.useCallback(() => {
    setSearchParams({}, { replace: true });

    setSearchResults(null);
    setErrorMessage(null);
    setExpandedContractIndex(null);
    setExpandedGroupIndex(null);

    methods.reset({
      searchCustomerGroupId: '',
      searchSapNumber: ''
    });
  }, [methods, setSearchParams]);

  const handleExpandClicked = (index: number) => () => {
    setExpandedContractIndex(null);
    setExpandedGroupIndex(prev => (prev === index ? null : index));
  };

  const renderCustomerSearchResults = (result: { group: Group; contracts: Contract[] }, index: number) => {
    const hasMultipleGroups = !!searchResults && searchResults.length > 1;

    return (
      <div className="mb-4" key={result.group.customerGroupId}>
        {searchResults && searchResults.length > 1 && (
          <div className="bg-elevation-1 hover:bg-elevation-2 px-4 py-2">
            <TableItem readonly onExpandClicked={handleExpandClicked(index)} expanded={expandedGroupIndex === index}>
              <TextInput title="Customer Name" initialValue={result.group.customerGroupName} readonly hideErrorControl />
              <TextInput title="Customer Type" initialValue={result.group.customerGroupTypeId} readonly hideErrorControl />
              <TextInput title="SAP Number" initialValue={result.group.sapCustomerNumber} readonly hideErrorControl />
            </TableItem>
          </div>
        )}

        {(expandedGroupIndex === index || (searchResults && searchResults.length === 1)) && (
          <div className={hasMultipleGroups ? 'pl-4 pt-4 border-l border-secondary-deep-blue-30' : ''}>
            <CustomerSearchResult group={result.group} contracts={result.contracts} expandedContractIndex={expandedContractIndex} setExpandedContractIndex={setExpandedContractIndex} />
          </div>
        )}
      </div>
    );
  };

  const didAutoSubmitRef = React.useRef(false);
  const lastSearchRef = React.useRef<CustomerSearch | null>(null);

  React.useEffect(() => {
    const valuesFromUrl = readSearchFromParams();
    const hasParams = !!valuesFromUrl.searchCustomerGroupId || !!valuesFromUrl.searchSapNumber;

    if (!hasParams) return;

    methods.reset(valuesFromUrl);

    if (isGroupDataLoading) return;

    if (didAutoSubmitRef.current) return;
    didAutoSubmitRef.current = true;

    handleSearch(valuesFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readSearchFromParams, isGroupDataLoading]);

  React.useEffect(() => {
    const onFocus = () => {
      const values = readSearchFromParams();
      const hasParams = !!values.searchCustomerGroupId || !!values.searchSapNumber;
      if (!hasParams) return;

      const last = lastSearchRef.current;
      const isSameSearch = last && last.searchCustomerGroupId === values.searchCustomerGroupId && last.searchSapNumber === values.searchSapNumber;

      if (isSameSearch) {
        return;
      }

      handleSearch(values);
    };

    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [readSearchFromParams, handleSearch]);

  return (
    <div>
      <PageHeading
        title="Customer Search"
        navigationButtons={[
          {
            icon: '/add.svg',
            title: 'Add Group',
            path: '/addGroup',
            disabled: !userInfo || userInfo.role === EUserRole.READONLY_USER
          }
        ]}
      />

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3 flex flex-col">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSearchSubmit)}>
              <FormSearchDropdown<CustomerSearch>
                title="Customer Group Name"
                placeholder="Select..."
                name="searchCustomerGroupId"
                elementsForDisplay={
                  groupData && typeof groupData === 'object'
                    ? groupData.map(data => ({
                        title: `${data.customerGroupName} (ID:${data.customerGroupId})`,
                        value: data.customerGroupId
                      }))
                    : []
                }
              />

              <FormTextInput<CustomerSearch> title="SAP Number" name="searchSapNumber" />

              <div className="flex flex-row justify-start gap-3">
                <FormButton type="submit" disabled={isGroupDataLoading || isSearchResultsLoading} primary>
                  Search
                </FormButton>

                <Button text="Reset" primary={false} onClick={handleReset} />
              </div>
            </form>
          </FormProvider>

          {errorMessage && <div className="pt-2 text-feedback-red">{errorMessage}</div>}
        </div>

        <div className="col-span-9">
          {isSearchResultsLoading && <div>Loading Search Results...</div>}
          {!isSearchResultsLoading && searchResults && searchResults.map(renderCustomerSearchResults)}
        </div>
      </div>
    </div>
  );
};
